<template>
    <div class="grid-contact-list-tool-bar">
        <div class="toolbar-row">
            <div class="left">
                <div class="status">
                    <a-tooltip placement="bottom" :title="`${$t('chat.gridContactListToolBar.chatStatus')}: ${currentStatusFullLabel}`">
                        <MinimalPopoverSelect
                            :value="effectiveState"
                            :options="statusOptions"
                            :show-icon="true"
                            :show-dropdown-arrow="false"
                            button-class="chip select-chip"
                            popover-class="toolbar-select-popover"
                            check-icon="done"
                            @change="onStatusChange"
                        />
                    </a-tooltip>
                </div>
            </div>
            <div class="spacer"></div>
            <div class="right">
                <a-tooltip placement="bottom" :title="`${$t('chat.gridContactListToolBar.sortBy')}: ${getSortTitle(selectedSortField)}`">
                    <MinimalPopoverSelect
                        :value="selectedSortField"
                        :options="sortOptions"
                        :show-icon="true"
                        :show-dropdown-arrow="false"
                        button-class="chip select-chip"
                        popover-class="toolbar-select-popover"
                        check-icon="done"
                        @change="onSortFieldChange"
                    />
                </a-tooltip>

                <a-tooltip placement="bottom" :title="selectedSortDir === 'DESC' ? $t('chatWindow.descending') : $t('chatWindow.ascending')">
                    <div class="chip chip--icon dir-chip" @click="toggleSortDir()">
                        <i class="material-icons">{{
                            selectedSortDir === "DESC" ? "arrow_downward" : "arrow_upward"
                        }}</i>
                    </div>
                </a-tooltip>
            </div>
        </div>
    </div>
</template>

<script>
import _ from "lodash";
import MinimalPopoverSelect from "./MinimalPopoverSelect.vue";
import { mapState, mapActions, mapGetters } from "vuex";

export default {
    name: "GridContactListToolbar",
    components: {
        MinimalPopoverSelect,
    },
    data: function () {
        return {};
    },
    computed: {
        ...mapState({
            selectedState: (state) => state.contact.selectedState,
            selectedSortField: (state) => state.contact.selectedSortField,
            selectedSortDir: (state) => state.contact.selectedSortDir,
            contactFilters: (state) => state.contact.filters || [],
            counts: (state) => state.contact.counts || {},
        }),
        ...mapGetters({
            selectedContactFilter: "contact/selectedFilter",
        }),
        hasLastConversationStatusFilter() {
            const currentFilter = this.contactFilters.find(f => f.id === this.selectedContactFilter?.id);
            if (!currentFilter || !currentFilter.filters) return false;
            return currentFilter.filters.some(f => f.field === "lastConversationStatus" && f.value);
        },
        effectiveState() {
            // lastConversationStatus filtresi varsa her zaman "all" döndür ama store'u değiştirme
            if (this.hasLastConversationStatusFilter) {
                return "all";
            }
            return this.selectedState;
        },
        statusOptions: function () {
            // lastConversationStatus filtresi varsa sadece "all" seçeneği göster
            if (this.hasLastConversationStatusFilter) {
                return [
                    {
                        label: this.$t("chat.gridContactListToolBar.all"),
                        value: "all",
                        icon: "layers",
                        fullLabel: this.$t("chat.gridContactListToolBar.all")
                    },
                ];
            }

            return [
                {
                    label: this.formatLabelWithCount(this.$t("chat.gridContactListToolBar.all"), this.counts.all || 0),
                    value: "all",
                    icon: "layers",
                    fullLabel: `${this.$t("chat.gridContactListToolBar.all")} (${this.counts.all || 0})`
                },
                {
                    label: this.formatLabelWithCount(this.$t("chat.gridContactListToolBar.open"), this.counts.open || 0),
                    value: "open",
                    icon: "lock_open",
                    fullLabel: `${this.$t("chat.gridContactListToolBar.open")} (${this.counts.open || 0})`
                },
                {
                    label: this.formatLabelWithCount(this.$t("chat.gridContactListToolBar.closed"), this.counts.closed || 0),
                    value: "closed",
                    icon: "lock",
                    fullLabel: `${this.$t("chat.gridContactListToolBar.closed")} (${this.counts.closed || 0})`
                },
            ];
        },
        currentStatusFullLabel() {
            const option = this.statusOptions.find(opt => opt.value === this.effectiveState);
            return option ? option.fullLabel : this.getStatusTitle(this.effectiveState);
        },
        sortOptions: function () {
            return [
                { label: this.$t("chat.gridContactListToolBar.lastActivity"), value: "lastActionTime" },
                { label: this.$t("chat.gridContactListToolBar.createdAt"), value: "createdAt" },
            ];
        },
    },
    methods: {
        ...mapActions({
            setSelectedState: "contact/setSelectedState",
            setSelectedSortField: "contact/setSelectedSortField",
            setSelectedSortDir: "contact/setSelectedSortDir",
        }),
        formatLabelWithCount(label, count) {
            return `${label} (${count})`;
        },
        getStatusTitle(value) {
            if (value === "all") return this.$t("chat.gridContactListToolBar.allStatus");
            if (value === "open") return this.$t("chat.gridContactListToolBar.open");
            if (value === "closed") return this.$t("chat.gridContactListToolBar.closed");
            return this.$t("chat.gridContactListToolBar.unknown");
        },
        onStatusChange(value) {
            // lastConversationStatus filtresi varsa store'u değiştirme
            if (this.hasLastConversationStatusFilter) {
                return; // Değişikliği engelle, store'a yazma
            }

            this.setSelectedState(value);
        },
        onSortFieldChange(value) {
            this.setSelectedSortField(value);
        },
        toggleSortDir() {
            this.setSelectedSortDir(this.selectedSortDir === "DESC" ? "ASC" : "DESC");
        },
        getSortTitle(value) {
            if (value === "lastActionTime") return this.$t("chat.gridContactListToolBar.lastActivity");
            if (value === "createdAt") return this.$t("chat.gridContactListToolBar.createdAt");
            return this.$t("chat.gridContactListToolBar.unknown");
        },
    },
};
</script>
<style lang="scss">
.grid-contact-list-tool-bar {
    padding: 10px;
    margin-bottom: 5px;

    .toolbar-row {
        display: flex;
        align-items: center;
        width: 100%;
        gap: clamp(0.25rem, 1vw, 0.5rem);
        min-width: 0;
    }

    .left,
    .right {
        display: flex;
        align-items: center;
        gap: clamp(0.25rem, 1vw, 0.5rem);
        flex-shrink: 1;
        min-width: 0;
    }

    .left {
        flex: 1;
        max-width: 50%;
        min-width: 0;
        .status {
            width: 100%;
            min-width: 0;

            .modern-select-trigger {
                max-width: 100%;
                display: block;
            }
        }
    }

    .right {
        flex-shrink: 0;
    }

    .spacer {
        flex: 1;
        min-width: 0.5rem;
    }

    .chip {
        display: inline-flex;
        align-items: center;
        gap: clamp(0.2rem, 0.8vw, 0.4rem);
        padding: clamp(0.25rem, 0.8vw, 0.35rem) clamp(0.4rem, 1.5vw, 0.7rem);
        border: 1px solid #c7d0d7;
        border-radius: 999px;
        background: #fff;
        color: #22272d;
        cursor: pointer;
        user-select: none;
        transition: background 0.15s ease, box-shadow 0.15s ease, transform 0.05s ease;
        font-size: clamp(11px, 2.5vw, 14px);
        white-space: nowrap;
        flex-shrink: 1;
        min-width: fit-content;
        width: auto;

        &.select-chip {
            max-width: 100%;
            min-width: 100px;

            .select-button {
                width: 100%;
                min-width: 0;
            }
        }

        &:hover {
            background: #f0f0f0;
        }

        &--icon {
            padding: clamp(0.25rem, 0.8vw, 0.35rem);
            width: clamp(1.6rem, 4.5vw, 2rem);
            height: clamp(1.6rem, 4.5vw, 2rem);
            justify-content: center;
            flex-shrink: 0;
        }

        strong {
            font-weight: 700;
        }

        .material-icons {
            font-size: clamp(15px, 3.5vw, 18px);
            line-height: 1;
            color: #22272d;
            flex-shrink: 0;
        }

        .button-text-v2 {
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            min-width: 40px;
            max-width: 100%;
            font-size: inherit;
        }
    }
}

/* Toolbar select popover styles */
.toolbar-select-popover {
    margin-top: -10px;

    .ant-popover-arrow {
        display: none;
    }

    .ant-popover-inner-content {
        padding: 6px 0;
        border: 1px solid #c7d0d7;
        border-radius: 5px;
    }

    .modern-select-dropdown {
        min-width: 200px;
        background: #fff;
    }

    .select-option {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
        padding: 8px 12px;
        cursor: pointer;
        color: #22272d;
        transition: all 0.15s ease;

        &:hover {
            background: #f0f0f0;
        }

        &.active {
            background: #f0f0f0;
            font-weight: 600;
        }
    }

    .select-option .option-content {
        display: flex;
        align-items: center;
        gap: 8px;
        flex: 1;
    }

    .select-option .option-icon {
        font-size: 16px;
        color: #22272d;
    }

    .select-option .option-label {
        font-size: 14px;
        white-space: nowrap;
    }

    .select-option .check-icon {
        margin-left: auto;
        font-size: 16px;
        color: #22272d;
    }
}
</style>
